import React, { useState } from "react";
import { MobileLayout } from "../components/layout/MobileLayout.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card.jsx";
import { Button } from "../components/ui/Button.jsx";
import { Input } from "../components/ui/Input.jsx";
import { Label } from "../components/ui/Label.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/Avatar.jsx";
import { Badge } from "../components/ui/Badge.jsx";
import { Switch } from "../components/ui/Switch.jsx";
import { ArrowLeft, Edit, Save, X } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { fetchLeads } from "../services/leadService.js";
import { useToast } from "../hooks/useToast.js";


export default function TeamMemberProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [member, setMember] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", role: "", status: true });
  const [stats, setStats] = useState({ assigned: 0, won: 0, prospect: 0, lost: 0 });

  const handleSave = () => {
    toast({
      title: "Profile Updated",
      description: "Team member profile has been updated successfully.",
    });
    setIsEditing(false);
  };

  const handleStatusToggle = (checked) => {
    setFormData(prev => ({ ...prev, status: checked }));
    const status = checked ? "activated" : "deactivated";
    toast({
      title: `Account ${status}`,
      description: `${formData.name || "User"}'s account has been ${status}.`,
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  React.useEffect(() => {
    const passed = location.state?.member;
    if (passed) {
      setMember(passed);
      setFormData({
        name: passed.name || "",
        email: passed.email || "",
        phone: passed.contact_number || "",
        role: passed.user_role || "",
        created_at: passed.created_at || "",
        status: true,
      });
    }
    const loadStats = async () => {
      try {
        const res = await fetchLeads({ page: 1, limit: 1, search: "" });

        const { dealStatusStats } = res;

        setStats({
          assigned: res.total,
          won: dealStatusStats?.won || 0,
          lost: dealStatusStats?.lost || 0,
          prospect: dealStatusStats?.prospect || 0,
        });
      } catch {
        setStats({ assigned: 0, won: 0, lost: 0, prospect: 0 });
      }
    };

    loadStats();
  }, [id]);

  return (
    <MobileLayout userRole="admin">
      <div className="space-y-4 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold">Team Member</h1>
          </div>

          <Button
            variant={isEditing ? "ghost" : "outline"}
            size="icon"
            onClick={() => setIsEditing(!isEditing)}
            className="rounded-full"
          >
            {isEditing ? <X className="h-5 w-5" /> : <Edit className="h-5 w-5" />}
          </Button>
        </div>

        <Card className="border-none shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <Avatar className="h-16 w-16">
                <AvatarImage src={member?.avatar || "/placeholder.svg"} alt={member?.name || "User"} />
                <AvatarFallback className="text-lg">
                  {(member?.name || "").split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h2 className="text-xl font-bold">{member?.name || "User"}</h2>
                <p className="text-muted-foreground">{member?.user_role === "sales_person" ? "Sales Person" : member?.user_role === "technician" ? "Technician" : member?.user_role}</p>
                <Badge variant="secondary" className="mt-1">Active</Badge>
              </div>
            </div>

            <div className="space-y-4">
              {isEditing ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select value={formData.role} onValueChange={(value) => handleInputChange("role", value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sales Representative">Sales Representative</SelectItem>
                        <SelectItem value="Senior Sales Rep">Senior Sales Rep</SelectItem>
                        <SelectItem value="Admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="status">Account Active</Label>
                    <Switch
                      id="status"
                      checked={formData.status}
                      onCheckedChange={handleStatusToggle}
                    />
                  </div>

                  <Button onClick={handleSave} className="w-full">
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Email</p>
                    <p className="font-medium">{member?.email || "—"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Phone</p>
                    <p className="font-medium">{member?.contact_number || "—"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Role</p>
                    <p className="font-medium">{member?.user_role === "sales_person" ? "Sales Person" : member?.user_role === "technician" ? "Technician" : member?.user_role}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Profile created at</p>
                    <p className="font-medium">    {member?.created_at
                    ? new Date(member.created_at).toLocaleString("en-IN", {
                          timeZone: "Asia/Kolkata",
                          year: "numeric",
                          month: "short", // Jan, Feb...
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })
                    : "Unavailable"}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Performance Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-success">{stats.won}</p>
                <p className="text-xs text-muted-foreground">Lead Won</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-warning">{stats.prospect}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-500">{stats.lost}</p>
                <p className="text-xs text-muted-foreground">Lead Lost</p>
              </div>
          </div>
          </CardContent>
        </Card>
      </div>
    </MobileLayout>
  );
}